import IntercomSdk, { boot, shutdown } from '@intercom/messenger-js-sdk';
import { useFeatureFlagEnabled } from 'posthog-js/react';
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { FCC } from '@eluve/components';
import { useNamedLogger } from '@eluve/logger';
import { FeatureFlagKeysLookup } from '@eluve/posthog';
import { useSession, useUserIdFromSession } from '@eluve/session-helpers';
import { formatHumanName } from '@eluve/utils';

import { appConfig } from './config';

const INTERCOM_DEFAULT_WHEN_POSTHOG_FAILS = false;

type IntercomStatus = {
  isIntercomReady: boolean;
};

const IntercomContext = createContext<IntercomStatus>({
  isIntercomReady: false,
});

export const useIntercomStatus = () => useContext(IntercomContext);

/**
 * Ensures the Intercom SDK script is loaded exactly once.
 * Must be called before any boot()/shutdown() calls.
 */
let sdkInitialized = false;
const ensureIntercomSdkInitialized = () => {
  if (!sdkInitialized) {
    IntercomSdk({ app_id: appConfig.VITE_INTERCOM_APP_ID });
    sdkInitialized = true;
  }
};

type IntercomUserData = {
  userId: string;
  email: string;
  name?: string;
  createdAt?: number;
};

type UseIntercomOptions = {
  /** Whether the caller is ready for Intercom to boot. Defaults to true. */
  isEnabled?: boolean;
  /** Logger name for debugging. */
  loggerName: string;
  /** User data for authenticated mode. If undefined, boots in anonymous mode. */
  userData?: IntercomUserData;
};

/**
 * Manages the complete Intercom lifecycle:
 * - Boots when feature flag and `isEnabled` are true.
 * - Shuts down when either is false.
 * - Cleans up on unmount.
 */
const useIntercom = (options: UseIntercomOptions): boolean => {
  const { userData, isEnabled = true, loggerName } = options;
  const logger = useNamedLogger(loggerName);
  const hasBootedRef = useRef(false);
  const [isReady, setIsReady] = useState(false);

  const isFeatureFlagEnabled =
    useFeatureFlagEnabled(FeatureFlagKeysLookup.INTERCOM_INTEGRATION) ??
    INTERCOM_DEFAULT_WHEN_POSTHOG_FAILS;

  const shouldBoot = isFeatureFlagEnabled && isEnabled;

  const bootConfig = useMemo(() => {
    const baseConfig = {
      app_id: appConfig.VITE_INTERCOM_APP_ID,
      hide_default_launcher: true,
    };
    if (userData) {
      return {
        ...baseConfig,
        user_id: userData.userId,
        email: userData.email,
        created_at: userData.createdAt,
        ...(userData.name && { name: userData.name }),
      };
    }
    return baseConfig;
  }, [userData]);

  useEffect(() => {
    let pollId: ReturnType<typeof setInterval> | undefined;
    let timeoutId: ReturnType<typeof setTimeout> | undefined;

    const clearTimers = () => {
      if (pollId) clearInterval(pollId);
      if (timeoutId) clearTimeout(timeoutId);
    };

    const shutdownIntercomIfBooted = (reason: string) => {
      if (hasBootedRef.current) {
        try {
          shutdown();
          logger.info(`Intercom shut down ${reason}`);
          hasBootedRef.current = false;
          setIsReady(false);
        } catch (error) {
          logger.error(`Failed to shut down Intercom ${reason}`, { error });
        }
      }
    };

    if (!shouldBoot) {
      shutdownIntercomIfBooted(
        `due to ${!isFeatureFlagEnabled ? 'feature flag being disabled' : 'caller disabling it'}`,
      );
      return;
    }

    try {
      ensureIntercomSdkInitialized();

      // If window.Intercom is undefined after SDK init, the IntercomSdk() call
      // was likely removed or broken — Intercom will not load for users.
      if (!window.Intercom) {
        logger.error('Intercom failed to initialize');
        return;
      }

      boot(bootConfig);
      hasBootedRef.current = true;

      // The Intercom SDK stub queues calls in window.Intercom.q until the
      // real widget script loads from the CDN and replaces the stub.
      // If the script is blocked (ad blocker, network), .q persists and
      // the messenger will never work — so we only mark ready once .q is gone.
      const isWidgetLoaded = () =>
        Boolean(window.Intercom && !('q' in window.Intercom));

      const onReady = () => {
        setIsReady(true);
        logger.info('Intercom booted successfully', {
          mode: userData ? 'authenticated' : 'anonymous',
        });
      };

      if (isWidgetLoaded()) {
        onReady();
      } else {
        pollId = setInterval(() => {
          if (isWidgetLoaded()) {
            clearTimers();
            onReady();
          }
        }, 250);

        timeoutId = setTimeout(() => {
          clearTimers();
          logger.warn('Intercom widget failed to load');
          hasBootedRef.current = false;
        }, 5000);
      }
    } catch (error) {
      logger.error('Failed to boot Intercom', { error });
    }

    return () => {
      clearTimers();
      shutdownIntercomIfBooted('on unmount');
    };
  }, [shouldBoot, isFeatureFlagEnabled, bootConfig, logger, userData]);

  return isReady;
};

export const IntercomProvider: FCC = ({ children }) => {
  const userId = useUserIdFromSession();
  const session = useSession();

  const hasSessionData = Boolean(userId && session?.email);

  const userData = useMemo(() => {
    if (!userId || !session?.email) {
      return undefined;
    }
    const fullName = formatHumanName(session.firstName, session.lastName);
    return {
      userId,
      email: session.email,
      name: fullName || undefined,
      createdAt: session.createdAt,
    };
  }, [
    userId,
    session?.email,
    session?.firstName,
    session?.lastName,
    session?.createdAt,
  ]);

  const isIntercomReady = useIntercom({
    isEnabled: hasSessionData,
    loggerName: 'IntercomProvider',
    userData,
  });

  const contextValue = useMemo(() => ({ isIntercomReady }), [isIntercomReady]);

  return (
    <IntercomContext.Provider value={contextValue}>
      {children}
    </IntercomContext.Provider>
  );
};

/**
 * Programmatically opens the Intercom messenger panel.
 * Safe to call even if Intercom hasn't loaded — silently no-ops.
 */
export const showIntercom = () => {
  if (window.Intercom) {
    window.Intercom('show');
  }
};
